/**
 * Which blocks of this estimate the customer reads, in which order, in whose
 * words.
 *
 * Jonathon, 2026-08-26: "I need to be able to toggle on and off what I want
 * visible on estimates in regards to time frame guarantees, unforeseen costs,
 * and items specific to you and your home" — and, asked whether it should be
 * those three or all of them, all of them. Then 2026-08-27, the rest of the
 * same thought: everything the customer reads must be editable, "remove it,
 * change where it appears, edit the text."
 *
 * So one list answers all three. A switch removes a block, the arrows move it,
 * and the block's own words are typed underneath it — the editors come in from
 * the screen that owns them, because the words live on `estimate_items` rows
 * and this component owns nothing.
 *
 * It sits on Present rather than in the builder (Jonathon, 2026-08-27: "remove
 * the 'What the customer reads' section from the Estimate Builder ... show it
 * only when Present Estimate is clicked"). The builder is the work; this is
 * the document, and it is read here.
 *
 * A block switched off is left out of the customer's copy entirely. Nothing is
 * deleted: the line items are still on the estimate, still on the work order,
 * still priced. This decides what is printed, and only that.
 */

import {
  applySectionOrder,
  packageSections,
  sectionKey,
  type Estimate,
  type PackageCopyLine,
} from "@roof-hawk/core";
import type { ReactNode } from "react";

import { t } from "../../i18n";

export interface ProposalBlocksProps {
  /** Decides one heading's wording: commercial reads "…and Your Property". */
  jobType: string | null | undefined;
  estimate: Pick<Estimate, "hidden_sections" | "section_order">;
  items: readonly PackageCopyLine[];
  onSave: (patch: Partial<Estimate>) => Promise<void>;
  /**
   * The words of each block, by `sectionKey`, drawn by whoever holds them.
   *
   * Optional: a screen that only wants the switches passes none, and the list
   * is a list of switches.
   */
  editors?: Record<string, ReactNode>;
  /**
   * Blocks to offer even when the estimate carries no line for them yet.
   *
   * The rep's own block for this house is typed on this screen, so on a
   * replacement that nobody has typed one into there is no row to find it by
   * — and without this it would be a box that only appears once you have
   * already used it.
   */
  alsoOffer?: readonly { key: string; heading: string }[];
  disabled?: boolean;
}

export function ProposalBlocks({
  jobType,
  estimate,
  items,
  onSave,
  editors,
  alsoOffer,
  disabled,
}: ProposalBlocksProps) {
  const found = packageSections(items, {
    jobType,
    order: estimate.section_order,
  });
  const missing = (alsoOffer ?? []).filter(
    (offer) => !found.some((section) => section.key === offer.key),
  );
  const order = estimate.section_order ?? [];
  const sections =
    missing.length === 0
      ? found
      : order.length > 0
        ? applySectionOrder([...found, ...missing], order)
        : [...found, ...missing];
  if (sections.length === 0) return null;

  // Read through `sectionKey` rather than trusted as stored: a row written
  // before the commercial wording existed holds a different spelling of the
  // same block, and it is still the same block.
  const hidden = new Set(
    (estimate.hidden_sections ?? []).map((key) => sectionKey(key)),
  );

  const toggle = (key: string) => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    void onSave({ hidden_sections: [...next] });
  };

  /*
   * Moving one block writes the whole order, every key of it.
   *
   * Storing only the block that moved would leave the rest to be inferred, and
   * the inference would change the day a template grows a block. What is
   * written is what is on screen, which is what the customer will read.
   */
  const move = (at: number, by: -1 | 1) => {
    const to = at + by;
    if (to < 0 || to >= sections.length) return;
    const keys = sections.map((section) => section.key);
    const [moved] = keys.splice(at, 1);
    keys.splice(to, 0, moved!);
    void onSave({ section_order: keys });
  };

  return (
    <div className="pblocks">
      <div className="chk-sub">{t("What the customer reads")}</div>
      {sections.map((section, at) => {
        const off = hidden.has(section.key);
        const editor = editors?.[section.key];
        return (
          <div className="pblock" key={section.key}>
            <div className="togline">
              <button
                className={`tog green${off ? "" : " on"}`}
                aria-label={section.heading}
                aria-pressed={!off}
                disabled={disabled}
                title={
                  off
                    ? t('Put "{name}" back on the customer\'s copy', {
                        name: section.heading,
                      })
                    : t('Leave "{name}" off the customer\'s copy', {
                        name: section.heading,
                      })
                }
                onClick={() => toggle(section.key)}
              />
              <span className={off ? "na-hint" : undefined}>
                {section.heading}
              </span>
              <span className="pblock-move">
                <button
                  type="button"
                  className="ghost"
                  aria-label={t('Move "{name}" up', { name: section.heading })}
                  disabled={disabled || at === 0}
                  onClick={() => move(at, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="ghost"
                  aria-label={t('Move "{name}" down', {
                    name: section.heading,
                  })}
                  disabled={disabled || at === sections.length - 1}
                  onClick={() => move(at, 1)}
                >
                  ↓
                </button>
              </span>
            </div>
            {/* A block that is switched off keeps its words: turning it back
                on is meant to bring back what was typed, not a blank box. */}
            {editor ? <div className="pblock-words">{editor}</div> : null}
          </div>
        );
      })}
      <div className="na-hint">
        {t(
          "Switched off means left out of the proposal — the line items stay on the estimate either way. The arrows change the order the customer reads them in, on this option only.",
        )}
      </div>
    </div>
  );
}
